import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ordersApi } from '../../services/OrderService';
import ReactDrawer from 'react-drawer';
import { Link } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import { Card, CardBody, CardTitle, CardText, Col, Container, Row, FormGroup, Nav, NavItem, NavLink, Label, ListGroup, ListGroupItem, Alert } from 'reactstrap';
import { AvForm, AvField } from 'availity-reactstrap-validation';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import { Dropdown } from 'semantic-ui-react';
import { ExternalLink, Copy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// Helpers
import { usStateOptions } from '../../helpers/country_helper';
import { countryOptionsPayoneer } from '../../helpers/country_helper_payoneer';
import { showToast } from '../../components/Toast';
import NumberFormat from 'react-number-format';
import Breadcrumbs from '../../components/Breadcrumb';
import useLoadingProgress from '../../hooks/useLoadingProgress';
import { MainContent } from '../../components/Table/MainContent';
import { getFormattedDateKST } from '../../helpers/current_date_helper';
import PageLoader from '../../components/Loaders/newPageLoader';

export default function PaymentRequest({ profileData }) { 
  const {
    data: baseData,
    isFetching: isLoadingBase,
    refetch,
  } = ordersApi.useFetchPaymentRequestsQuery(profileData._id, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
  });
  const [getPaymentRequests, { data: unfilteredData, isFetching: isLoadingUnfiltered }] = ordersApi.useLazyFetchPaymentRequestsQuery();
  const [getFilteredPaymentRequests, { data: filteredData, isFetching: isLoadingFiltered }] = ordersApi.useLazyFilterPaymentRequestsQuery();
  const [generatePaymentRequest, { isLoading: isGenerating }] = ordersApi.useGeneratePaymentRequestMutation();
  // ── which view is active? 'base' | 'unfiltered' | 'filtered'
  const [view, setView] = useState('base');

  const [requests, setPaymentRequestsData] = useState([]);
  const [request, setSelectedRequest] = useState({ invoiceSum: '' });
  const [showFee, setShowFee] = useState(false);
  const [filteredRequests, setFilteredRequests] = useState(null);
  const [filteredStatusesRequests, setFilteredStatusesRequests] = useState([]);
  const [filters, setNoDbFilters] = useState([]);
  const [accessToken, setAccessToken] = useState(null);
  const [managers, setManagersData] = useState([]);
  const [years, setYearsData] = useState([]);
  const [months, setMonthsData] = useState([]);

  const [period, setPeriodData] = useState([]);
  const [isChecked, setChecked] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [errors, setErrors] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');

  const [modalPayment, setPaymentModal] = useState(false);
  const [modalInfo, setInfoModal] = useState(false);

  const activeData = view === 'filtered'
    ? filteredData
    : view === 'unfiltered'
      ? unfilteredData
      : baseData;

  const loading = view === 'filtered'
    ? isLoadingFiltered
    : view === 'unfiltered'
      ? isLoadingUnfiltered
      : isLoadingBase;

  const metaSource = useMemo(() => {
    return unfilteredData || baseData || null;
  }, [unfilteredData, baseData]);

  const mergedData = useMemo(() => {
    if (view === 'filtered') {
      const filteredReqs = filteredData?.requests ?? [];
      // if we have a full meta source, keep it and just replace requests
      return metaSource ? { ...metaSource, requests: filteredReqs } : { requests: filteredReqs };
    }
    // for 'unfiltered' and 'base', just use the actual datasets
    return view === 'unfiltered' ? (unfilteredData || baseData) : baseData;
  }, [view, filteredData, unfilteredData, baseData, metaSource]);

  useEffect(() => {
    const applyFilters = () => {
      let result = [...requests];
      if (filters?.status?.length > 0) {
        result = result.filter(request => filters?.status.includes(request?.paymentStatus?.[request?.paymentStatus?.length-1].status));
      }
      setFilteredStatusesRequests(result);
    };

    applyFilters();
  }, [filters, requests]); 

  const { progress, step, visible } = useLoadingProgress({
    isLoading: loading,
    isFetching: loading,
    isLoadingUnfiltered,
    isLoadingFiltered,
  });

  useEffect(() => {
    if (!mergedData) return;
    const { requests = [], managers = [], categories = [], languages = [], countries = [], period = {}, accessToken = {}, years = [], months = [] } = mergedData;

    setPaymentRequestsData((prev) => (JSON.stringify(prev) !== JSON.stringify(requests) ? [...requests] : prev));
    setManagersData((prev) => (JSON.stringify(prev) !== JSON.stringify(managers) ? [...managers] : prev));
    setPeriodData((prev) => (prev !== period ? period : prev));
    setAccessToken((prev) => (prev !== accessToken?.variableValue ? accessToken?.variableValue : prev));
    setYearsData((prev) => (JSON.stringify(prev) !== JSON.stringify(years) ? [...years] : prev));
    setMonthsData((prev) => (JSON.stringify(prev) !== JSON.stringify(months) ? [...months] : prev));
    // setYearsData((prev) => {
    //   if (!Array.isArray(years)) return prev; // safeguard
    //   return JSON.stringify(prev) !== JSON.stringify(years) ? [...years] : prev;
    // });
    // setMonthsData((prev) => {
    //   if (!Array.isArray(months)) return prev; // safeguard
    //   return JSON.stringify(prev) !== JSON.stringify(months) ? [...months] : prev;
    // });
  }, [JSON.stringify(mergedData)]);
  useEffect(() => {
    if (period?.year && period?.month) {
      setFilterFormValues({
        year: period.year,
        month: period.month,
        manager: '',
        statuses: []
      });
    }
  }, [period]);
  const [cardHeight, setCardHeight] = useState(0);
  const cardRef = useRef(null);
  useEffect(() => {
    if (!cardRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newHeight = entry.contentRect.height;
        if (newHeight > 0) setCardHeight(newHeight);
      }
    });
    resizeObserver.observe(cardRef.current);
    return () => resizeObserver.disconnect();
  }, []);
  useEffect(() => {
    const applyFilters = () => {
      let result = [...requests];

      if (filters?.orderTypes?.length > 0) {
        const allowedValues = filters.orderTypes;
        result = result.filter((order) => allowedValues.includes(order.paymentLinkCreated));
      }

      // if (filters?.accountTypes?.length > 0) {
      //   result = result.filter(user => filters?.accountTypes.includes(user.accType));
      // }
      setFilteredRequests(result);
    };

    applyFilters();
  }, [filters, requests]);

  const showAllOrders = async () => {
    setChecked(prev => {
      const next = !prev;
      const id = next ? profileData?._id : 'all';  // decide which id you use for "my" vs "all"
      getPaymentRequests(id);          // fire lazy unfiltered list
      setView('unfiltered');           // show the unfiltered lazy result
      return next;
    });
  };

  const [filterFormValues, setFilterFormValues] = useState({});
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
  };
  const filterFormSubmit = async () => {
    const createOrderBtn = document.getElementById('create-order-btn');
    const myOrdersBtn = document.getElementById('myOrders');
    if (createOrderBtn && myOrdersBtn) {
      const isSameDate = filterFormValues.year === period.year && filterFormValues.month === period.month;
      createOrderBtn.disabled = !isSameDate;
      myOrdersBtn.disabled = !isSameDate;
    }
    try {
      const id = isChecked ? profileData?._id : 'all' 
      await getFilteredPaymentRequests({id, filterFormValues}).unwrap();
      setView('filtered');
    } catch (err) {
      console.log('raw error:', err);
    }
  }
  const filterFormReset = async () => {
    const createOrderBtn = document.getElementById('create-order-btn');
    const myOrders = document.getElementById('myOrders');
  
    if (createOrderBtn) createOrderBtn.disabled = false;
    if (myOrders) myOrders.disabled = false;
    setFilterFormValues({
      year: period.year,
      month: period.month,
      manager: '',
      statuses: []
    });
    const id = isChecked ? profileData?._id : 'all';
    try {
      await getPaymentRequests(id).unwrap();
      setView('unfiltered');
    } catch (err) {
      console.log('reset error:', err);
    }
  };
  /**
   * Toggles the visibility state of the payment modal.
   * Uses a callback to invert the current modal state.
   *
   * @function
   * @returns {void}
   */
  const togglePaymentModal = useCallback(() => {
    setPaymentModal((prev) => !prev);
  }, []);
  /**
   * Toggles the visibility state of the info modal.
   * Uses a callback to invert the current modal state.
   *
   * @function
   * @returns {void}
   */
  const toggleOrderInfoModal = useCallback(() => {
    setInfoModal((prev) => !prev);
  }, []);
  const [addFields, setAddInputFields] = useState([]);
  const handleNewPaymentClick = async (e) => {
    if (!accessToken || accessToken === '') {
      const redirectUri = encodeURIComponent(process.env.REACT_APP_REDIRECT_URI);
      const scope = 'read write';
      const responseType = 'code';
      const url = `https://login.payoneer.com/api/v2/oauth2/authorize?client_id=${process.env.REACT_APP_CLIENT_ID}&redirect_uri=${redirectUri}&scope=${scope}&response_type=${responseType}`;
      window.open(url, '_blank');
    } else {
      const newAddFields = [];
      newAddFields.push({
        invoiceSum: '',
        invoiceCurrency: 'USD',
        showFee: false
      });
      setAddInputFields(newAddFields);
      togglePaymentModal();
      setSelectedRequest([]);
      setErrorMsg('')
      setErrors([])
    }
  };

  const handleInputChange = (value) => {
    setSelectedCountry(value);
  };
  const handleStateChange = (value) => {
    setSelectedState(value);
  };
  /**
   * Adds a new input field object to the current state based on the selected delivery service.
   * 
   * If the selected delivery service (`delServ`) is 'Others', a new logistic input field object is added.
   * Otherwise, a new delivery input field object is added.
   * The function copies the existing input fields, appends a new field object with default values,
   * and updates the state with the new array.
   */
  const addInputField = () => {
    // Copy the current input fields state
    const newAddFields = [...addFields];
    // Push a new input field state
    newAddFields.push({
      invoiceSum: '',
      invoiceCurrency: 'USD',
      showFee: false
    });
    // Set the new state
    setAddInputFields(newAddFields);
  };
  /**
   * Removes an input field from the addFields array at the specified index and updates the state.
   *
   * @param {number} index - The index of the input field to remove.
   */
  const removeInputField = (index) => {
    // Copy the current input fields state
    const newAddFields = [...addFields];
    // Remove the input field at the specified index
    newAddFields.splice(index, 1);
    // Update state with modified input fields
    setAddInputFields(newAddFields);
  };

  const handleChange = (index, name, value) => {
    const updated = [...addFields];
    updated[index][name] = value;

    if (name === 'invoiceSum') {
      updated[index].showFee = value && Number(value) > 0;
    }

    setAddInputFields(updated);
  };
  const bannedCountries = ['ru', 'kp', 'by', 'ir'];
  const enhancedCountryOptions = countryOptionsPayoneer.map(opt => {
    if (bannedCountries.includes(opt.value)) {
      return {
        ...opt,
        disabled: true,
        text: `${opt.text} (Under Sanction)`
      };
    }
    return opt;
  });


  const modalPaymentLinkContent = (
    <>
      {errorMsg && <Alert color="danger">{errorMsg}</Alert>}
      {errors.length > 0 && errors.map((msg, i) => (<Alert color="danger" key={i}>{msg.message}</Alert>))}
      <Row className='ms-3 me-3'>
        {/* <Col md={1}></Col> */}
        <Col md={3}></Col>
        <Col md={6}>
          <FormGroup className='mb-3'>
            <AvField
              type='select'
              name='manager'
              id='manager'
              className='form-select'
              label='Manager'
              errorMessage='Please select manager'
              validate={{
                required: {
                  value: true,
                  errorMessage: 'Please select manager'
                }
              }}
              // value={profileData._id + '|' + profileData.bitrixID}
              value={request.manager ? request.manager._id + '|' + request.manager.bitrixID : profileData._id + '|' + profileData.bitrixID}
            >
              {managers.map((manager) => {
                return (
                  <option key={manager._id} value={manager._id + '|' + manager.bitrixID}>
                    {manager.fname} {manager.sname}
                  </option>
                );
              })}
            </AvField>
          </FormGroup>
        </Col>
        <Col md={3}></Col>
      </Row>
      <hr />
      <Row className='ms-3 me-3'>
        {/* <Col md={1}></Col> */}
        <Col md={3}>
          <FormGroup className='mb-3'>
            <AvField
              name='clientFName'
              label='Client First Name'
              type='text'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={request.clientFName}
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className='mb-3'>
            <AvField
              name='clientSName'
              label='Client Last Name'
              type='text'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={request.clientSName}
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className='mb-3'>
            <AvField
              name='email'
              label='Email'
              type='email'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={request.email}
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className='mb-3'>
            <AvField
              name='description'
              label='Name and Order # from ERP'
              type='text'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={request.description}
            />
          </FormGroup>
        </Col>
        {/* <Col md={1}></Col> */}
      </Row>
      <Row className='ms-3 me-3'>
        {/* <Col md={1}></Col> */}
        <Col md={3}>
          <FormGroup className='mb-3'>
            <AvField
              name='clientName'
              label='Company'
              type='text'
              autoComplete='off'
              placeholder=''
              // validate={{
              //   required: {
              //     value: true
              //   }
              // }}
              value={request.clientName}
            />
            <p style={{ fontSize: '0.79rem', color: '#6c757d', fontWeight:'600' }}>Client name will be used if Company empty</p>
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className='mb-3'>
            <AvField
              name='link'
              label='Link'
              type='link'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={request.link}
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className='mb-3'>
            <Label htmlFor='country'>Country</Label>
            <Dropdown
              placeholder='Select Country'
              fluid
              search
              selection
              options={enhancedCountryOptions}
              name='country'
              defaultValue={selectedCountry}
              disabled={bannedCountries.includes(selectedCountry)}
              onChange={(e, data) => handleInputChange(data.value)}
              id='country'
            />
          </FormGroup>
        </Col>
        {selectedCountry === 'us' && (
          <Col md={3}>
            <FormGroup className='mb-3'>
              <Label htmlFor='state'>State</Label>
              <Dropdown
                placeholder='Select State'
                fluid
                search
                selection
                options={usStateOptions}
                name='state'
                defaultValue={selectedCountry}
                onChange={(e, data) => handleStateChange(data.value)}
                id='state'
              />
            </FormGroup>
          </Col>
        )}
      </Row>
      {addFields.map((field, index) => (  
      <Row className='ms-3 me-3' key={index}>
        <Col md={3}></Col>
        <Col md={3}>
          <FormGroup className='mb-3'>
            <AvField
              name={`invoiceSum${index}`}
              label='Invoice Sum'
              type='number'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={field.invoiceSum}
              onChange={(e) => handleChange(index, 'invoiceSum', e.target.value)}
            />
            {field.showFee && (
              <Row>
                <Col md={8} className="ps-3 pt-2 text-start">
                  <h3 className="area-text-label">Estimated Fees (4%):</h3>
                </Col>
                <Col md={4} className="ps-0 pt-2 text-start">
                  <h3 className="area-text-label"><span className=""><NumberFormat value={(Number(field.invoiceSum) * 0.04).toFixed(2)} displayType='text' thousandSeparator={true} prefix='' /></span></h3>
                </Col>
              </Row>
            )}
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className='mb-3'>
            <AvField
              type='select'
              name={`invoiceCurrency${index}`}
              id='invoiceCurrency'
              className='form-select'
              label='Currency'
              errorMessage='Please select currency'
              validate={{
                required: {
                  value: true,
                  errorMessage: 'Please select currency'
                }
              }}
              defaultValue="USD"
              onChange={(e) => handleChange(index, 'invoiceCurrency', e.target.value)}
              value={field.invoiceCurrency}
            >
              <option value='USD'>USD</option>
              <option value='EUR'>EUR</option>
              <option value='GBP'>GBP</option>
              <option value='JPY'>JPY</option>
              <option value='CAD'>CAD</option>
              <option value='AUD'>AUD</option>
              <option value='AED'>AED</option>
              <option value='MXN'>MXN</option>
              <option value='SGD'>SGD</option>
              <option value='HKD'>HKD</option>
            </AvField>
          </FormGroup>
        </Col>
        {index === addFields.length - 1 && (
          <Col md={1} className='d-flex align-items-center mt-4'>
            <Link className='text-success' to='#'>
              <i className='mdi mdi-plus font-size-24' onClick={addInputField}></i>
            </Link>
          </Col>
        )}
        {index !== 0 && (
          <Col md={1} className='d-flex align-items-center mt-4'>
            <Link className='text-danger' to='#'>
              <i className='mdi mdi-delete font-size-24' id='deletetooltip' onClick={() => removeInputField(index)}></i>
            </Link>
          </Col>
        )}
      </Row>
      ))}



      {/* <Row>
        <Col md={3}></Col>
        <Col md={6}>
          <Dropzone accept='.jpeg,.jpg,.png,.pdf' onDrop={(acceptedFiles) => handleAcceptedFiles(acceptedFiles)}>
            {({ getRootProps, getInputProps }) => (
              <div className='dropzone'>
                <div className='dz-message needsclick' {...getRootProps()}>
                  <input {...getInputProps()} />
                  <div className='mb-3'>
                    <i className='display-4 text-muted bx bxs-cloud-upload' />
                  </div>
                  <h4>Drop files here or click to upload.</h4>
                </div>
              </div>
            )}
          </Dropzone>
        </Col>
        <Col md={3}></Col>
      </Row> */}
      <hr />
    </>
  );

  const getCountryName = (country) => countryOptionsPayoneer.find((o) => o.value === country).text;
  const getStateName = (state) => {
    const match = usStateOptions.find((o) => o.value === state);
    return match ? match.text : '';
  };

  const handleNewPaymentLinkSubmit = async (e, values) => {
    e.preventDefault();
    let allSuccessful = true;
    // if (!selectedFile) {
    //   alert('Please upload a PDF file.');
    //   return;
    // }
    const description = `${values['description']} ${(() => {
      const m = managers.find((m) => m._id === values['manager'].split('|')[0]);
      return m ? `, ${m.fname[0]}${m.sname[0]}${m.sname.match(/\d+/)?.[0] || ''}` : '';
    })()}`;
    let invoiceNo = '';

    if (values['description'].startsWith('SBO-')) {
      // Take the first part before space/∙
      invoiceNo = values['description'].split(/[ ∙]/)[0];
    } else {
      // Extract the first number sequence
      const match = values['description'].match(/\d+/);
      invoiceNo = match ? match[0] : '';
    }

    for (const pm of addFields) {
      const payoneerData = {
        client_reference_id: `${invoiceNo}_${new Date().toISOString().replace(/[:.]/g, '-')}`,
        amount: pm.invoiceSum,
        currency: pm.invoiceCurrency,
        description: description,
        payer_details: {
          type: 'COMPANY',
          company: {
            name: values['clientName'] === '' ? values['clientFName']+' '+values['clientSName'] : values['clientName'],
            url: values['link']
          },
          contact: {
            first_name: values['clientFName'],
            last_name: values['clientSName'],
            phone: '',
            phone_country: '',
            email: values['email']
          },
          address: {
            country: selectedCountry.toUpperCase(),
            ...(selectedState !== '' && { state: selectedState })
          },
        },
        fee_pay_by: 'payer',
        payment_due_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10)
      };
      const dbData = {
        accessToken: accessToken,
        manager: values['manager'].split('|')[0],
  
        clientReferenceId: `${invoiceNo}_${new Date().toISOString().replace(/[:.]/g, '-')}`,
  
        clientFName: values['clientFName'],
        clientSName: values['clientSName'],
        clientName: values['clientName'] === '' ? values['clientFName']+' '+values['clientSName'] : values['clientName'],
        email: values['email'],
        link: values['link'],
        invoiceNo: invoiceNo,
        invoiceSum: pm.invoiceSum,
        invoiceCurrency: pm.invoiceCurrency,
        description: description,
        paymentDueDate: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
        country: selectedCountry,
        state: selectedState ? selectedState : '',
        paymentStatus: [ { date: getFormattedDateKST(), status: 'Payment request pending' } ],
        paymentLinkCreated: true,
  
        startDate: period.startDate,
        year: period.year,
        month: period.month
      };
      // Create a FormData object
      const formData = new FormData();
      // Append the file
      // formData.append('file', selectedFile);
      // Append the payload as JSON string
      formData.append('payoneerData', JSON.stringify(payoneerData));
      formData.append('dbData', JSON.stringify(dbData));
      try {
        const id = isChecked ? profileData?._id : 'all' 
        const response = await generatePaymentRequest({id, formData, dbData}).unwrap();
        // togglePaymentModal();
        // setOrder({});
      } catch (err) {
        allSuccessful = false;
        console.log('errors:', err.data.errors);
        const labelMap = {
          first_name: "Client First Name",
          last_name: "Client Last Name",
          url: "Link"
        };
        const formattedErrors = err.data.errors.map(err => {
          const newTarget = labelMap[err.target] || err.target;
          let newMessage = err.message.replace(err.target, newTarget);
  
          // Add extra help if it's the Link field
          if (err.target === "url") {
            newMessage += " If you put Facebook as a link, make sure to remove https://www.";
          }
  
          return {
            ...err,
            target: newTarget,
            message: newMessage
          };
        });
        setErrorMsg('Failed to create payment link:');
        setErrors(formattedErrors);
        break;
      }
    }
    if (allSuccessful) {
      togglePaymentModal();
    }
  };

  const [position, setPosition] = useState('right');
  const [openDr, setOpenDr] = useState(false);

  /**
   * Toggles the visibility of the right drawer.
   * Sets the drawer position to 'right' and switches its open/closed state.
   */
  const toggleRightDrawer =() => {
    setPosition('right')
    setOpenDr(prev => !prev);
  }
  /**
   * Closes the drawer by setting its open state to false.
   * Typically used as an event handler for closing UI drawers or side panels.
   */
  const onDrawerClose = () => setOpenDr(false);
  
  const handleViewDetailsClick = (request) => {
    setSelectedRequest(request)
    toggleRightDrawer();
  }
  const handleCopyRequestClick = (request) => {
    setSelectedRequest(request);
    setSelectedCountry(request.country)
    setSelectedState(request.state)
    togglePaymentModal();
    const newAddFields = [];
    newAddFields.push({
      invoiceSum: '',
      invoiceCurrency: 'USD',
      showFee: false
    });
    setAddInputFields(newAddFields);
    setErrorMsg('');
  }  

  const columns = [
    {
      text: 'ID',
      dataField: '_id',
      sort: true,
      editable: false,
      formatter: (cell, row, rowIndex) => rowIndex + 1
    },
    {
      text: 'Created At',
      dataField: 'createdAt',
      csvExport: true,
      sort: true,
      editable: false,
      formatter: (cellContent, order) => (
        <div className=''>
          <h5 className='font-size-16 mb-1'>
           {new Date(order.createdAt).toLocaleDateString('en-GB')}
          </h5>
        </div>
      )
    },
    {
      text: 'Invoice No',
      dataField: 'invoiceNo',
      sort: true,
      csvExport: false,
      hidden: false,
      editable: false,
      formatter: (cellContent, order) => {
        return (
          <div className='p-2'>
            <p className='mb-0'>
              {order.invoiceNo}
            </p>
          </div>
        );
      }
    },
    {
      text: 'Sent To',
      dataField: 'clientFName',
      sort: true,
      csvExport: false,
      hidden: false,
      editable: false,
      formatter: (cellContent, order) => {
        return (
          <div className='p-2'>
            <p className='mb-0'>
              {order.clientFName} {order.clientSName}
            </p>
          </div>
        );
      }
    },
    {
      text: 'Payment Request',
      dataField: 'paymentLink',
      sort: true,
      csvExport: false,
      hidden: false,
      editable: false,
      formatter: (cellContent, order) => (
        <div className='pt-4 ps-2 pb-4'>
          <h5 className='font-size-16 mb-1 d-flex align-items-left'>
            <span className='d-inline-flex align-items-left'>
              {order.paymentLink && (
                <>
                  <a href={order.paymentLink} target='_blank' rel='noopener noreferrer' className='ms-2 me-2 d-flex align-items-center'>
                    <strong className='pe-2'>Payment Request Link </strong> <ExternalLink size={16} />
                  </a>
                  <Copy
                    size={18}
                    color='#497ea7'
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();

                      const htmlToCopy = `Hello,
    Here is the link for your payment:
    <a href="${order.paymentLink}"> ${order.paymentLink}</a>

    Thank you`;
                      if (navigator.clipboard && window.ClipboardItem) {
                        const blobInput = new ClipboardItem({
                          'text/html': new Blob([htmlToCopy], { type: 'text/html' }),
                          'text/plain': new Blob([htmlToCopy.replace(/<[^>]+>/g, '')], { type: 'text/plain' })
                        });

                        navigator.clipboard
                          .write([blobInput])
                          .then(() => showToast('success', 'Copied to clipboard'))
                          .catch(() => showToast('error', 'Failed to copy'));
                      } else {
                        // Fallback for older/HTTP browsers
                        const textarea = document.createElement('textarea');
                        textarea.value = htmlToCopy.replace(/<[^>]+>/g, ''); // strip HTML for plain text copy
                        textarea.style.position = 'fixed';
                        textarea.style.opacity = 0;
                        document.body.appendChild(textarea);
                        textarea.select();

                        try {
                          if (document.execCommand('copy')) {
                            showToast('success', 'Copied to clipboard');
                          } else {
                            showToast('error', 'Failed to copy');
                          }
                        } catch (err) {
                          showToast('error', 'Failed to copy');
                        }

                        document.body.removeChild(textarea);
                      }
                    }}
                  />

                </>
              )}
            </span>
          </h5>
          {/* <strong style={{ display: 'block' }}>{order.paymentRequestId}</strong> */}
          {/* <strong style={{ display: 'block' }}>{order.paymentStatus}</strong> */}
        </div>
      )
    },
    {
      text: 'Amount',
      dataField: 'invoiceSum',
      sort: true,
      csvExport: false,
      hidden: false,
      editable: false,
      formatter: (cellContent, order) => {
        return (
          <div className='p-2'>
            <p className='mb-0'>
              {order.invoiceSum} {order.invoiceCurrency}
            </p>
          </div>
        );
      }
    },
    {
      text: 'Due By',
      dataField: 'dueDate',
      sort: true,
      csvExport: false,
      hidden: false,
      editable: false,
      formatter: (cellContent, order) => {
        return (
          <div className='p-2'>
            <p className=' mb-0'>
              {new Date(order.paymentDueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric'})}
            </p>
            {new Date(order.paymentDueDate) < new Date() && (
              <span className="ms-2 badge bg-danger">Past Due</span>
            )}
          </div>
        );
      }
    },
    {
      text: 'Payment Details',
      dataField: 'clientName',
      sort: true,
      csvExport: false,
      hidden: false,
      editable: false,
      formatter: (cellContent, order) => {
        return (
          <div className='info-width p-2'>
            <p className='text-muted mb-0'>
              Payment ID: <b>{order.paymentId}</b>
            </p>
            <p className='text-muted mb-0'>
              Sender: <b>{order.clientName}</b>
            </p>
            <p className='text-muted d-flex mb-0'>
              Website: {order.link && <a href={order.link} target='_blank' rel='noopener noreferrer' className='ms-2 me-2 d-flex align-items-center'>
                <b>Website link</b>
              </a>}
            </p>
            <p className='text-muted mb-0'>
              Client Name:{' '}
              <b>
                {order.clientFName} {order.clientSName}
              </b>
            </p>
            <p className='text-muted mb-0'>
              Email: <b>{order.email}</b>
            </p>
            <p className='text-muted mb-0'>
              {/* Country: <b>{order.country}</b> */}
              Country: <b>{order.country && getCountryName(order.country)}</b>
            </p>
            <p className='text-muted mb-0'>
              Invoice Sum: <b>{order.invoiceSum} {order.invoiceCurrency}</b>
            </p>
            <p className='text-muted mb-0'>
              Invoice Number: <b>{order.invoiceNo}</b>
            </p>
          </div>
        );
      }
    },
    {
      text: 'Status',
      dataField: 'paymentStatus',
      sort: true,
      csvExport: false,
      hidden: false,
      editable: false,
      formatter: (cellContent, order) => {
        return (
          // order.paymentStatus && (
          //   <div className='pt-4 pb-4'>
          //     <div
          //       className={`p-2 rounded-pill text-center ${
          //         ['payment deposited', 'payment on its way to you'].includes(order.paymentStatus.toLowerCase())
          //           ? 'btn-success'
          //           : ['payment request pending'].includes(order.paymentStatus.toLowerCase())
          //           ? 'btn-warning'
          //           : ['payment request canceled', 'payment declined', 'request dismissed', 'payment reversed'].includes(order.paymentStatus.toLowerCase())
          //           ? 'btn-danger'
          //           : 'btn-secondary'
          //       }`}
          //     >
          //       {['payment deposited', 'payment on its way to you'].includes(order.paymentStatus.toLowerCase()) && (
          //         <i className='bx bx-check-double font-size-16 align-middle me-2'></i>
          //       )}
          //       {['payment request pending'].includes(order.paymentStatus.toLowerCase()) && (
          //         <i className='bx bx-time-five font-size-16 align-middle me-2'></i>
          //       )}
          //       {['payment request canceled', 'payment declined', 'request dismissed', 'payment reversed'].includes(order.paymentStatus.toLowerCase()) && (
          //         <i className='bx bx-x-circle font-size-16 align-middle me-2'></i>
          //       )}
          //       <span className="font-size-16 fw-bold">
          //         {order.paymentStatus}
          //       </span>
          //     </div>

          //   </div>
          // )
          order.paymentStatus && (
            <div className='pt-4 pb-4'>
              {(() => {
                // Determine currentStatus text
                const currentStatus = Array.isArray(order.paymentStatus)
                  ? order.paymentStatus[order.paymentStatus.length - 1]?.status || ''
                  : order.paymentStatus;

                const normalizedStatus = currentStatus.toLowerCase();

                // Determine style class
                const statusClass = ['payment deposited', 'payment on its way to you', 'deposited by ach. verification completed'].includes(normalizedStatus)
                  ? 'btn-success'
                  : ['payment request pending', 'deposited by ach. waiting for verification'].includes(normalizedStatus)
                  ? 'btn-warning'
                  : ['payment request canceled', 'payment declined', 'request dismissed', 'payment reversed'].includes(normalizedStatus)
                  ? 'btn-danger'
                  : 'btn-secondary';

                // Determine icon
                const iconClass = ['payment deposited', 'payment on its way to you', 'deposited by ach. verification completed'].includes(normalizedStatus)
                  ? 'bx bx-check-double'
                  : ['payment request pending', 'deposited by ach. waiting for verification'].includes(normalizedStatus)
                  ? 'bx bx-time-five'
                  : ['payment request canceled', 'payment declined', 'request dismissed', 'payment reversed'].includes(normalizedStatus)
                  ? 'bx bx-x-circle'
                  : '';

                return (
                  <div className={`p-2 rounded-pill text-center ${statusClass}`}>
                    {iconClass && <i className={`${iconClass} font-size-16 align-middle me-2`}></i>}
                    <span className="font-size-16 fw-bold">
                      {currentStatus}
                    </span>
                  </div>
                );
              })()}
            </div>
          )
        );
      }
    },
    {
      text: 'Manager',
      dataField: 'manager.workEmail',
      csvExport: true,
      sort: true,
      editable: false,
      formatter: (cellContent, order) => (
        <div className='slname-width'>
          <h5 className='font-size-16 mb-1'>
            <span>
              <div>
                {!order.manager.profilePic ? (
                  <div className='avatar-sm m-2 d-inline-block'>
                    <span className='avatar-title rounded-circle'>{order?.manager?.fname.charAt(0)}</span>
                  </div>
                ) : (
                  <img className='rounded-circle avatar-sm m-2' src={order?.manager?.profilePic} alt='profile pic' />
                )}
                <span>
                  {order.manager.fname}
                  &nbsp;
                  {order.manager.sname}
                </span>
              </div>
            </span>
          </h5>
        </div>
      )
    },
    {
      dataField: 'menu',
      isDummyField: true,
      editable: false,
      text: 'Action',
      formatter: (cellContent, order) => (
        <div className='d-flex gap-3'>
          <Link className='text-success' to='#'>
            <i className="mdi mdi-eye font-size-18" onClick={() => handleViewDetailsClick(order)}></i>
          </Link>
          <Link className='text-info' to='#'>
            <i className='mdi mdi-content-copy font-size-18' onClick={() => handleCopyRequestClick(order)} ></i>
          </Link>
        </div>
      )
    }
  ];

  const defaultSorted = [
    {
      dataField: 'date',
      order: 'asc'
    }
  ];

  const filterCustomContent = (
    <>
      <FormGroup className='me-1 new_input'>
        <AvField type='select' name='year' id='year' className='form-select' onChange={handleFilterChange} value={filterFormValues.year}>
          {years.map((year, index) => (
            <option key={index} value={year} className='fw-bold fs-5'>
              {year}
            </option>
          ))}
        </AvField>
      </FormGroup>
      <FormGroup className='me-1 new_input'>
        <AvField type='select' name='month' className='form-select' onChange={handleFilterChange} value={filterFormValues.month}>
          {months.map((month, index) => (
            <option key={index} value={month.month} className='fw-bold fs-5'>
              {month.month}
            </option>
          ))}
        </AvField>
      </FormGroup>
      <FormGroup className='me-1 new_input'>
        <AvField
          type='select'
          name='manager'
          id='manager'
          onChange={handleFilterChange}
          className='form-select'
          // value={isChecked ? profileData._id + '|' + profileData.bitrixID : filterFormValues.manager}
          // value={profileData._id + '|' + profileData.bitrixID}
          // value={request.manager ? request.manager._id + '|' + request.manager.bitrixID : profileData._id + '|' + profileData.bitrixID}
        >
          <option value=''>All</option>
          {managers.map((manager) => {
            return (
              <option key={manager._id} value={manager._id + '|' + manager.bitrixID}>
                {manager.fname} {manager.sname}
              </option>
            );
          })}
        </AvField>
      </FormGroup>
      <FormGroup className='me-1'>
        <button className='btn btn-success save-user'>
          Filter
        </button>
      </FormGroup>
    </>
  );
// const paymentStatusOptions = [...new Set(users.map(u => u.accType))].map(type => ({ key: type, label: type, icon: '', colorClass: 'bg-primary' }));
  const paymentStatusOptions = [
    {
      key: "Payment request pending",
      label : "Payment request pending",
      icon: '',
      colorClass: 'bg-primary',
      date : "",
      type: 'default'
    },
    {
      key: "Payment made by client",
      label : "Payment made by client",
      icon: '',
      colorClass: 'bg-primary',
      date : "",
      type: 'default'
    },
    {
      key: "Payment being processed",
      label : "Payment being processed",
      icon: '',
      colorClass: 'bg-primary',
      date : "",
      type: 'default'
    },
    {
      key: "Payment on its way to you",
      label : "Payment on its way to you",
      icon: '',
      colorClass: 'bg-primary',
      date : "",
      type: 'default'
    },
    {
      key: "Payment deposited",
      label : "Payment deposited",
      icon: '',
      colorClass: 'bg-primary',
      date : "",
      type: 'default'
    },
    {
      key: "Deposited by ACH. Waiting for verification",
      label : "Deposited by ACH. Waiting for verification",
      icon: '',
      colorClass: 'bg-primary',
      date : "",
      type: 'extra'
    },
    {
      key: "Deposited by ACH. Verification completed",
      label : "Deposited by ACH. Verification completed",
      icon: '',
      colorClass: 'bg-primary',
      date : "",
      type: 'extra'
    },
    {
      key: "Payment request canceled",
      label : "Payment request canceled",
      icon: '',
      colorClass: 'bg-primary',
      date : "",
      type: 'extra'
    },
    {
      key: "Payment declined",
      label : "Payment declined",
      icon: '',
      colorClass: 'bg-primary',
      date : "",
      type: 'extra'
    },
    {
      key: "Payment reversed",
      label : "Payment reversed",
      icon: '',
      colorClass: 'bg-primary',
      date : "",
      type: 'extra'
    },
  ]
  // 1) Helpers
  const optionsByKey = Object.fromEntries(paymentStatusOptions.map(o => [o.key, o]));
  const defaultOptions = paymentStatusOptions.filter(o => o.type === 'default');

  // Build a map of the latest date per status from the request
  const statusDates = new Map();
  (request?.paymentStatus || []).forEach(s => statusDates.set(s.status, s.date));
  const MADE_KEY = "Payment made by client";
  const PROC_KEY = "Payment being processed";
  const procDate = statusDates.get(PROC_KEY);
  if (procDate) statusDates.set(MADE_KEY, procDate);

  // Is there any extra-type status in the request?
  const hasExtraStatuses = (request?.paymentStatus || []).some( s => optionsByKey[s.status]?.type === 'extra');
  // 2) Build the display list according to the rules
  let displayList = [];
  if (hasExtraStatuses) {
    // Show only the statuses present in request.paymentStatus (extras + any defaults that appear there)
    const seen = new Set();
    displayList = (request?.paymentStatus || [])
      .map(s => {
        const opt = optionsByKey[s.status];
        if (opt) {
          return { ...opt, date: statusDates.get(opt.key) || "", checked: true };
        }
        // Unknown status fallback
        return { key: s.status, label: s.status, type: 'extra', date: s.date, checked: true };
      })
      .filter(item => {
        if (seen.has(item.key)) return false;
        seen.add(item.key);
        return true;
      });
  } else {
    // Show defaults and mark checks up to the latest default stage present
    const highestIdx = Math.max(
      -1,
      ...(request?.paymentStatus || [])
        .map(s => defaultOptions.findIndex(o => o.key === s.status))
        .filter(i => i >= 0)
    );

    displayList = defaultOptions.map((o, idx) => ({
      ...o,
      checked: idx <= highestIdx,
      date: statusDates.get(o.key) || ""
    }));
  }
  // Find the last checked index
  const lastCheckedIndex = displayList.map(d => !!d.checked).lastIndexOf(true);  
  const hasFilterApplied = filters?.status?.length > 0;
  const data = hasFilterApplied ? filteredStatusesRequests : filteredRequests || requests;

  return (
    <React.Fragment>
      <PageLoader progress={progress} step={step} visible={visible} />
      <div className='page-content'>
        <MetaTags>
          <title>Payoneer Payment Requests</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title='Price List' breadcrumbItem='Payoneer Payment Requests' />
          <Row className='card-section' >
            <Col lg='12'>
              <div className='pcss3t logtb pcss3t-effect-scale pcss3t-theme-1'>
                <ul>
                  <AnimatePresence key='1' exitBeforeEnter>
                    <motion.li
                      className={`tab-content tab-content-1 typography`}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.3 }}
                      style={{ display: 'block' }}
                    >
                      <div id='card-section' ref={cardRef}>
                        <Row className='card-section'>
                          <Col lg='12'>
                            <Card>
                              <CardBody className='cardBodySticky payoneerCard'>
                                <ToolkitProvider keyField='_id' columns={columns} 
                                  data={data}
                                   search>
                                  {(props) => (
                                    <React.Fragment>
                                      <MainContent
                                        page={{
                                          pg: 'client_orders',
                                          props: props,
                                          cardHeight: cardHeight,
                                          showSearch: true,
                                          showCSV: false,
                                          showSwitch: true,
                                          showInTblFltr: true,
                                          showDbFilter: true,
                                          showBtn: true,
                                          showScndBtn: false,
                                          columns: props.baseProps.columns,
                                          data: props.baseProps.data,
                                          defaultSorted,
                                          filter: null,
                                          cellEdit: null
                                        }}
                                        notificationSettings={{ showNotification: true, keyword: 'payoneer', module: 'Bot Helper Notification' }}
                                        myOrdersOptions={{
                                          isChecked: isChecked,
                                          setChecked: setChecked,
                                          showAllOrders: showAllOrders,
                                          access: profileData.permissions.includes('battlefieldPayoneerAllOrdersAccess')
                                        }}
                                        // table data opotions
                                        tableOptions={{ 
                                          includeSelect: false, 
                                          calculateFooter: false, 
                                          manualColumnWidthsIndex: [1,2,4,5,6,7,8,9,10 ], 
                                          manualColumnWidths: [130,180,250,130,130,370,320,270]
                                        }}
                                        // filter options
                                        filterOptions={{ status: paymentStatusOptions }}
                                        filterDbOptions={{ filterFormValues, filterCustomContent, setFilterFormValues, filterFormSubmit, filterFormReset }}
                                        setNoDbFilters={setNoDbFilters}
                                        // options for adding/updating modal and button
                                        btnCrtModalOptions={{
                                          btnClass: 'create-order-btn',
                                          btnLabelCrt: 'New Payment Request',
                                          btnIcon: 'mdi mdi-cash-multiple me-1',
                                          modalSize: 'xl',
                                          modalTitle: 'Generate New Payment Request',
                                          modalBtnLabel: 'Generate New Payment Request',
                                          modalAdd: modalPayment,
                                          toggleAddModal: togglePaymentModal,
                                          handleNewClick: handleNewPaymentClick,
                                          modalContent: modalPaymentLinkContent,
                                          handleSubmit: handleNewPaymentLinkSubmit
                                        }}
                                      />
                                      <ReactDrawer open={openDr} className='drawer-open payoneer-drawer' position={position} onClose={onDrawerClose}>
                                        <div className='text-center mt-4 mb-4'><h2 style={{ color: '#252526' }}><b>Payment Request Details</b></h2></div>
                                        <Card className="shadow-lg ms-4 me-4 payoneer-drawer-card">
                                          <div className="text-center p-4 payoneer-drawer-card-logo">
                                            <i className="bx bx-buildings text-white" ></i>
                                          </div>
                                          <CardBody className="text-center payoneer-drawer-card-body">
                                            <p className="name font-size-23 fw-bold">{request.clientFName} {request.clientSName} {request.clientName}</p>
                                            <p className="price fw-bold">{<NumberFormat value={parseFloat(request.invoiceSum).toFixed(2)} displayType='text' thousandSeparator={true} prefix='' />} <small className="text-muted">{request.invoiceCurrency}</small></p>
                                            <p className="status">
                                              {request?.paymentStatus?.[request?.paymentStatus?.length-1].status}
                                            </p>
                                            <p className="due">
                                              Due by {new Date(request.paymentDueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric'})}
                                            </p>
                                          </CardBody>
                                        </Card>
                                        <Nav pills className="ms-4 me-4 mt-3 mb-3 payoneer-drawer-tabs">
                                          <NavItem>
                                            <NavLink active href="#">Timeline</NavLink>
                                          </NavItem>
                                          <NavItem>
                                            <NavLink href="#">Details</NavLink>
                                          </NavItem>
                                        </Nav>
                                        <ListGroup className="ms-4 me-4 mt-3 mb-3 payoneer-drawer-timeline" flush>
                                          <ListGroupItem>
                                            <Row className="pb-2">
                                              <Col xs="1" className="p-0">
                                                <div className='edge edge_passed'></div>
                                                <div className='check ms-3'>
                                                  <svg viewBox="0 0 32 32" ><path d="M31.68 9.056c0-0.512-0.224-0.96-0.544-1.376l-2.752-2.784c-0.32-0.416-0.864-0.64-1.408-0.64s-0.96 0.224-1.376 0.544l-13.216 13.344-5.984-5.888c-0.32-0.32-0.864-0.512-1.376-0.512s-0.96 0.192-1.408 0.512l-2.752 2.688c-0.32 0.32-0.544 0.832-0.544 1.376s0.224 0.96 0.544 1.376l10.112 10.144c0.32 0.32 0.864 0.544 1.408 0.544s0.96-0.224 1.376-0.544l2.784-2.784 14.592-14.72c0.32-0.32 0.544-0.832 0.544-1.28z"></path></svg>
                                                </div>
                                              </Col>
                                              <Col xs="11" className='p-0'>
                                                <p className="name ps-4 mb-0 font-size-12 fw-bold">{new Date(request?.createdAt?.replace(/([+-]\d{2})(\d{2})$/,'$1:$2')).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}</p>
                                                <p className="name ps-4 font-size-16 fw-bold">You sent a payment request</p>
                                              </Col>
                                            </Row>
                                            {displayList.map((item, index) => {
                                              const isLastRow = index === displayList.length - 1;
                                              const isChecked = !!item.checked;
                                              const isLastChecked = index === lastCheckedIndex;

                                              // Show edge below this node?
                                              // - Hide on the very last row
                                              // - Hide under the last checked (so blue line “stops” there)
                                              const showEdgeBelow = !isLastRow && index !== lastCheckedIndex;

                                              // Edge color:
                                              // - Blue (edge_passed) for nodes BEFORE lastChecked
                                              // - Grey for nodes AFTER lastChecked
                                              // (Note: we already hide it for the lastChecked node itself)
                                              const edgeClass =
                                                index < lastCheckedIndex ? 'edge edge_passed' : 'edge';

                                              return (
                                                <Row key={item.key} className="mt-4 pb-2">
                                                  <Col xs="1" className="p-0">
                                                    {/* {showEdgeBelow && <div className={`${edgeClass} ${!isChecked && 'edge_empty'}`}></div>} */}
                                                    <div className={`${edgeClass} ${!isChecked && 'edge_empty'}`}></div>
                                                    <div className={`${isChecked ? 'check ms-3' : 'check_empty'} ${isLastChecked ? 'check_last' : ''}`}>
                                                      {/* Show check icon only for checked items EXCEPT the last checked (solid blue circle instead) */}
                                                      {isChecked && !isLastChecked && (
                                                        <svg viewBox="0 0 32 32">
                                                          <path d="M31.68 9.056c0-0.512-0.224-0.96-0.544-1.376l-2.752-2.784c-0.32-0.416-0.864-0.64-1.408-0.64s-0.96 0.224-1.376 0.544l-13.216 13.344-5.984-5.888c-0.32-0.32-0.864-0.512-1.376-0.512s-0.96 0.192-1.408 0.512l-2.752 2.688c-0.32 0.32-0.544 0.832-0.544 1.376s0.224 0.96 0.544 1.376l10.112 10.144c0.32 0.32 0.864 0.544 1.408 0.544s0.96-0.224 1.376-0.544l2.784-2.784 14.592-14.72c0.32-0.32 0.544-0.832 0.544-1.28z"></path>
                                                        </svg>
                                                      )}
                                                    </div>
                                                  </Col>

                                                  <Col xs="11" className="p-0">
                                                    <p className="name ps-4 mb-0 font-size-12 fw-bold">
                                                      {item.date || ""}
                                                    </p>
                                                    <p className="name ps-4 font-size-16 fw-bold">
                                                      {item.label}
                                                    </p>
                                                  </Col>
                                                </Row>
                                              );
                                            })}
                                          </ListGroupItem>
                                        </ListGroup>
                                      </ReactDrawer>
                                    </React.Fragment>
                                  )}
                                </ToolkitProvider>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    </motion.li>
                  </AnimatePresence>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
